import { useAddRedemption, useRemoveRedemption } from '../use-loyalty'
import { toastEachWarning } from '../../Modifiers/ToastNotification/ToastNotification'

const onCartOutOfStock = (message) => {
  toastEachWarning({ warnings: [message] })
}

/**
 * @typedef {Object} UseLoyaltyRedemptionValues
 * @property {function} addRedemption - Apollo mutation callback for applying the redemption
 * @property {function} removeRedemption - Apollo mutation callback for removing the redemption
 * @property {boolean} addLoading - Apollo mutation loading state for applying redemption
 * @property {boolean} removeLoading - Apollo mutation loading state for removing redemption
 * @property {?Error} addError - Error yielded from addRedemption mutation
 * @property {?Error} removeError - Error yielded from removeRedemption mutation
 * @property {?string} redemptionGuidOnCart - Current loyalty redemption guid on cart. Null if nothing redeemed.
 * @property {?number} remainingBalance - Remaining balance after discount has been applied to cart. Null if nothing redeemed.
*/

/**
 * @typedef {Object} Redemption
 * @property {number} redemptionAmount
 * @property {string} redemptionGuid
*/

/**
 * @param {Object} options - Hook options
 * @param {Object} options.cart - A cart object
 * @param {Redemption} options.redemption - A loyalty redemption object
 * @return {UseLoyaltyRedemptionValues} - Values object for useLoyalytRedemption hook
*/
export const useLoyaltyRedemption = ({ cart, redemption }) => {
  if (!cart) {
    throw new Error('"useLoyaltyFieldsetState" is missing named argument, "cart"')
  }

  const [
    addRedemption,
    { error: addError, loading: addLoading }
  ] = useAddRedemption({ onCartOutOfStock })

  const [
    removeRedemption,
    { error: removeError, loading: removeLoading }
  ] = useRemoveRedemption({ onCartOutOfStock })

  let redemptionGuidOnCart = null
  let remainingBalance = null
  if (redemption && cart.order.discounts.loyaltyDiscount) {
    redemptionGuidOnCart = cart.order.discounts.loyaltyDiscount.guid
    remainingBalance = redemption.redemptionAmount - cart.order.discounts.loyaltyDiscount.amount
  }

  return {
    addRedemption: () => {
      return addRedemption(redemption.redemptionGuid)
    },
    removeRedemption: () => {
      return removeRedemption(redemptionGuidOnCart)
    },
    addLoading,
    removeLoading,
    addError,
    removeError,
    redemptionGuidOnCart,
    remainingBalance
  }
}
